/**
 * MetricGauge — Medidor semicircular de las tarjetas KPI.
 *
 * QUÉ HACE: dibuja un arco 0–`max` (por defecto 0–100) con barrido animado al
 *   montar y en cada cambio de valor, la cifra central y, debajo, una etiqueta
 *   de nivel y una leyenda corta. Es la versión "de tarjeta" del gauge del
 *   explorador (`InsightGaugeChart`), reutilizable por cualquier KPI que mida un
 *   puntaje o un cumplimiento. Lo usa la prop `gauge` de MetricCard.
 * QUÉ INVOCA: `useAnimatedNumber` (barrido/cifra animados).
 * QUÉ EXPORTA: MetricGauge, MetricGaugeProps.
 * QUÉ NO DEBE HACER: no calcula el puntaje final ni accede a datos.
 */

import { useAnimatedNumber } from "@/hooks/useAnimatedNumber";

/** Tono semántico del arco (alineado a `MetricBadgeTone`). */
type MetricGaugeTone = "success" | "warning" | "critical" | "info";

export interface MetricGaugeProps {
  /** Valor actual del medidor. */
  value: number;
  /** Tope del medidor (por defecto 100). */
  max?: number;
  /** Tono semántico del arco y de la etiqueta de nivel. */
  tone?: MetricGaugeTone;
  /** Etiqueta de nivel bajo la cifra (p. ej. "Excelente"). */
  label?: string;
  /** Leyenda corta al pie (p. ej. "5 de 8 áreas en buen estado"). */
  caption?: string;
  className?: string;
}

const TONE_COLOR: Record<MetricGaugeTone, string> = {
  success: "#10b981",
  warning: "#f59e0b",
  critical: "#ef4444",
  info: "#0ea5e9",
};

const TONE_TEXT_CLASS: Record<MetricGaugeTone, string> = {
  success: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  critical: "text-rose-600 dark:text-rose-400",
  info: "text-sky-600 dark:text-sky-400",
};

/** Tono derivado del porcentaje cuando no se indica uno explícito. */
const toneFromRatio = (ratio: number): MetricGaugeTone =>
  ratio >= 80 ? "success" : ratio >= 60 ? "warning" : "critical";

export const MetricGauge = ({
  value,
  max = 100,
  tone,
  label,
  caption,
  className = "",
}: MetricGaugeProps) => {
  const safeMax = max > 0 ? max : 100;
  const ratio = Math.max(0, Math.min(1, value / safeMax));
  const pct = ratio * 100;
  const resolvedTone = tone ?? toneFromRatio(pct);
  /** Barrido/cifra animados: barren desde 0 y migran al cambiar de valor. */
  const { value: animated } = useAnimatedNumber(Math.max(0, value));
  const sweepPct = Math.max(0, Math.min(100, (animated / safeMax) * 100));
  /** Semicírculo superior: de (30,100) a (170,100) con radio 70. */
  const arc = "M 30 100 A 70 70 0 0 1 170 100";

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col items-center justify-center text-slate-800 dark:text-slate-100 ${className}`}
    >
      <svg
        viewBox="0 0 200 112"
        className="w-full max-w-[200px]"
        role="img"
        aria-label={`${label ?? "Puntaje"} ${Math.round(value)} de ${safeMax}`}
      >
        <path
          d={arc}
          pathLength={100}
          fill="none"
          stroke="rgb(226 232 240)"
          strokeWidth={14}
          strokeLinecap="round"
          className="dark:opacity-30"
        />
        <path
          d={arc}
          pathLength={100}
          fill="none"
          stroke={TONE_COLOR[resolvedTone]}
          strokeWidth={14}
          strokeLinecap="round"
          strokeDasharray={`${sweepPct} 100`}
        />
        <text
          x="100"
          y="82"
          textAnchor="middle"
          fontSize="34"
          fontWeight="800"
          fill="currentColor"
        >
          {Math.round(animated)}
        </text>
        <text
          x="100"
          y="100"
          textAnchor="middle"
          fontSize="12"
          fontWeight="600"
          fill="currentColor"
          opacity="0.5"
        >
          / {safeMax}
        </text>
      </svg>
      {label && (
        <p
          className={`mt-0.5 text-[11px] font-bold ${TONE_TEXT_CLASS[resolvedTone]}`}
        >
          {label}
        </p>
      )}
      {caption && (
        <p className="mt-0.5 text-[9px] leading-tight text-slate-400 dark:text-slate-500">
          {caption}
        </p>
      )}
    </div>
  );
};
